/** @format */

import { apiClient } from "@/api/axiosClient.ts";
import { handleApiError } from "./common.ts";
import type { PaginatedResponse, PaginationParams } from "./common.ts";
import type { OcularEyeFieldName } from "@/lib/ocularExamFields";

// Visit domain types aligned with /clinic/visits/ endpoints
export type VisitStatus =
  | "scheduled"
  | "in_progress"
  | "finished"
  | "completed"
  | "cancelled"
  | "no_show";

export interface VisitListItem {
  id: string; // UUID
  patient: string; // patient UUID
  patient_name: string;
  doctor: number;
  doctor_name: string;
  visit_date: string; // YYYY-MM-DD
  visit_type: string; // e.g., "routine"
  department: "ophthalmology" | "orl";
  status: VisitStatus;
  chief_complaint?: string;
  diagnosis?: string;
  diagnosis_extra_notes?: string;
  conclusions?: string;
  follow_up_date?: string; // YYYY-MM-DD
  is_completed?: string; // backend returns formatted string flag
  requires_followup?: string; // formatted string flag
  created_at?: string; // ISO datetime
  updated_at?: string; // ISO datetime
  visit_type_display: string;
}

type OcularEyeFieldValues = Partial<Record<OcularEyeFieldName, string>>;

export interface OcularExam extends OcularEyeFieldValues {
  visit: string; // visit UUID
  has_elevated_iop?: string;
  iop_summary?: string;
  glasses_prescription?: string;
  vitreous_body?: string;
  retina?: string;
  objective_refraction?: string;
  subjective_refraction?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Prescription {
  id: string; // UUID
  visit: string; // visit UUID
  prescription_type: "medication" | "glasses" | string;
  medication_name?: string;
  dosage?: string;
  frequency?: string;
  duration?: string;
  route?: "oral" | string;
  instructions?: string[] | null;
  notes?: string;
  is_active?: boolean;
  formatted_dosage?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Refraction {
  id?: string; // UUID
  visit?: string; // visit UUID
  close_dist_interpupillary?: number;
  away_dist_interpupillary?: number;
  add?: number;
  notes?: string;
  measurements: RefractionMeasurement[];
  created_at?: string;
  updated_at?: string;
}

export interface VisitDocument {
  id: string; // UUID
  document: string; // absolute URL
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export interface VisitDetail extends VisitListItem {
  treatments?: string;
  follow_up_notes?: string;
  duration_minutes?: number;
  ocular_exam?: OcularExam;
  prescriptions?: Prescription[];
  refractions?: RefractionData;
  has_ocular_exam?: string;
  has_prescriptions?: string;
  has_refraction_data?: string;
  intervention?: EyeIntervention | null;
}

export type VisitsListResponse = PaginatedResponse<VisitListItem>;

export interface RefractionMeasurement {
  eye: "L" | "R";
  distance: "AWAY" | "CLOSE";
  sf?: number;
  cil?: number;
  ax?: number; // 0-180
}

export interface RefractionData {
  close_dist_interpupillary?: number;
  away_dist_interpupillary?: number;
  add?: number;
  notes?: string;
  measurements: RefractionMeasurement[];
}

export interface PrescriptionData {
  // visit: string; // UUID
  prescription_type?: string;
  medication_name: string;
  dosage?: string;
  frequency?: string;
  duration?: string;
  route?: string;
  instructions?: string[] | null;
  notes?: string;
  is_active?: boolean;
}

export type EyeInterventionEye = "OD" | "OS" | "OU";
export type EyeInterventionProcedureType =
  | "CAT"
  | "LASIK"
  | "GLAU"
  | "RET"
  | "COR"
  | "OCUL"
  | "YAG"
  | "INJ"
  | "OTHER";
export type EyeInterventionComplication =
  | "NONE"
  | "PCR"
  | "VIT"
  | "COR"
  | "OTHER";

export interface EyeIntervention {
  eye: EyeInterventionEye;
  procedure_type: EyeInterventionProcedureType;
  procedure_type_other?: string | null;
  complication?: EyeInterventionComplication;
  complication_details?: string | null;
  implants_used?: string | null;
  post_op_instructions?: string[];
  assistants?: number[];
  nasal_presentation?: string[];
  nasal_presentation_other?: string | null;
  delta_k?: string | null;
  usg?: string | null;
  oct?: string | null;
  iol?: string | null;
}

export interface OcularExaminationData extends OcularEyeFieldValues {
  // visit: string; // UUID
  vitreous_body?: string;
  retina?: string;
  glasses_prescription?: string;
  objective_refraction?: string;
  subjective_refraction?: string;
}

export interface CreateVisitRequest {
  patient: string; // UUID
  doctor: number;
  visit_date: string; // YYYY-MM-DD
  visit_type?: string;
  department?: "ophthalmology" | "orl";
  status?: VisitStatus;
  chief_complaint?: string;
  diagnosis?: string;
  diagnosis_extra_notes?: string;
  treatments?: string;
  conclusions: string;
  follow_up_date?: string; // YYYY-MM-DD
  follow_up_notes?: string;
  duration_minutes?: number;
  ocular_exam?: OcularExaminationData;
  prescriptions?: PrescriptionData[];
  refractions?: RefractionData;
  intervention?: EyeIntervention;
}

export interface UpdateVisitRequest extends CreateVisitRequest {}

export interface VisitsQueryParams extends PaginationParams {
  patient_id: string; // required filter
  status?: VisitStatus;
  search?: string;
  diagnosis?: string;
}

export const visitsApi = {
  /**
   * Get paginated list of visits (patient_id is required)
   * GET /clinic/visits/
   */
  async getVisits(params: VisitsQueryParams): Promise<VisitsListResponse> {
    // Runtime guard: backend requires patient_id to be provided for listing
    if (!params?.patient_id || params.patient_id.trim() === "") {
      throw new Error("patient_id is required to list visits");
    }
    try {
      const response = await apiClient.get<VisitsListResponse>(
        "/clinic/visits/",
        { params },
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to fetch visits");
    }
  },

  /**
   * Get a single visit by ID
   * GET /clinic/visits/{id}/
   */
  async getVisit(id: string, patientID: string): Promise<VisitDetail> {
    try {
      const response = await apiClient.get<VisitDetail>(
        `/clinic/visits/${id}/?patient_id=${patientID}`,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to fetch visit");
    }
  },

  /**
   * Create a new visit
   * POST /clinic/visits/
   */
  async createVisit(data: CreateVisitRequest): Promise<VisitDetail> {
    try {
      const response = await apiClient.post<VisitDetail>(
        "/clinic/visits/",
        data,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to create visit");
    }
  },

  /**
   * Update a visit (PUT)
   * PUT /clinic/visits/{id}/
   */
  async updateVisit(
    id: string,
    patientID: string,
    data: UpdateVisitRequest,
  ): Promise<VisitDetail> {
    try {
      const response = await apiClient.put<VisitDetail>(
        `/clinic/visits/${id}/?patient_id=${patientID}`,
        data,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to update visit");
    }
  },

  /**
   * Partially update a visit (PATCH)
   * PATCH /clinic/visits/{id}/
   */
  async partialUpdateVisit(
    id: string,
    data: Partial<UpdateVisitRequest>,
    patientID?: string,
  ): Promise<VisitDetail> {
    try {
      const url = patientID
        ? `/clinic/visits/${id}/?patient_id=${patientID}`
        : `/clinic/visits/${id}/`;
      const response = await apiClient.patch<VisitDetail>(url, data);
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to update visit");
    }
  },

  /**
   * Delete a visit
   * DELETE /clinic/visits/{id}/
   */
  async deleteVisit(id: string): Promise<void> {
    try {
      await apiClient.delete(`/clinic/visits/${id}/`);
    } catch (error) {
      handleApiError(error, "Failed to delete visit");
    }
  },

  /**
   * Upload documents for a visit
   * PUT /clinic/visits/{id}/upload-documents/
   */
  async uploadVisitDocuments(id: string, files: File[]): Promise<void> {
    if (!id) {
      throw new Error("visit id is required to upload documents");
    }
    if (!files || files.length === 0) {
      throw new Error("At least one file is required to upload documents");
    }

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("documents", file, file.name);
      });

      await apiClient.put(`/clinic/visits/${id}/upload-documents/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      handleApiError(error, "Failed to upload visit documents");
    }
  },

  /**
   * Get uploaded documents for a visit
   * GET /clinic/visits/{id}/documents/
   */
  async getVisitDocuments(id: string): Promise<VisitDocument[]> {
    if (!id) {
      throw new Error("visit id is required to fetch visit documents");
    }
    try {
      const response = await apiClient.get<VisitDocument[]>(
        `/clinic/visits/${id}/documents/`,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to fetch visit documents");
    }
  },

  /**
   * Delete a visit document
   * DELETE /clinic/visit-documents/{documentId}/
   */
  async deleteVisitDocument(documentId: string): Promise<void> {
    if (!documentId) {
      throw new Error("document id is required to delete visit document");
    }
    try {
      await apiClient.delete(`/clinic/visit-documents/${documentId}/`);
    } catch (error) {
      handleApiError(error, "Failed to delete visit document");
    }
  },

  /**
   * Get all visits for a patient (no pagination)
   * GET /clinic/visits/ with large page_size
   */
  async getAllVisitsForPatient(patientId: string): Promise<VisitDetail[]> {
    if (!patientId || patientId.trim() === "") {
      throw new Error("patient_id is required to list visits");
    }

    try {
      // Fetch with a large page size to get all visits
      const response = await apiClient.get<VisitsListResponse>(
        "/clinic/visits/",
        {
          params: {
            patient_id: patientId,
            page_size: 1000,
            page: 1,
          },
        },
      );

      // Get the list of visit IDs
      const visitList = response.data.results || [];

      // Fetch full details for each visit
      const visitDetailsPromises = visitList.map((visit) =>
        this.getVisit(visit.id, patientId),
      );

      const allVisits = await Promise.all(visitDetailsPromises);

      // Sort by visit_date descending (most recent first)
      return allVisits.sort((a, b) => {
        const dateA = new Date(a.visit_date).getTime();
        const dateB = new Date(b.visit_date).getTime();
        return dateB - dateA; // descending order
      });
    } catch (error) {
      handleApiError(error, "Failed to fetch all visits");
    }
  },
};
